import React from 'react'
import Select from 'components/global/CUK/AsyncSelect/Select'
import { components } from 'react-select'
import { useTranslation } from 'react-i18next'
import { Typography, useTheme, Box, ClickAwayListener } from '@mui/material'
import StateIcon from 'components/global/StateIcon'

interface Offer {
  name: string
  count: number
}

interface Choice {
  label: string
  value: string
  count: number
  id: number
}

const DropdownIndicator = ({ ...props }) => {
  const theme = useTheme()
  return (
    <Box mr={1}>
      {props.selectProps.menuIsOpen ? (
        <StateIcon
          iconlabel={'angle-up'}
          iconweight={'regular'}
          iconcolor={theme.palette.primary.main}
        />
      ) : (
        <StateIcon 
          iconweight={'regular'} 
          iconlabel={'angle-down'} 
          iconcolor={theme.palette.text.primary}
        />
      )}
    </Box>
  )
}

const ValueContainer = ({ ...props }) => {
  const theme = useTheme()
  const selectedValuesCount = props.getValue().length
  const { menuIsOpen } = props.selectProps

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <components.ValueContainer {...props}>
      <Box>
        <Typography
          sx={{
            color: menuIsOpen ? '#3B82F6' : theme.palette.grey[500],
            fontSize: '12px',
            paddingLeft: theme.spacing(1.5),
            fontWeight: 500,
          }}
        >
          {selectedValuesCount === 0
            ? props.selectProps.placeholder
            : `${selectedValuesCount} sélectionnés`}
        </Typography>
      </Box>
    </components.ValueContainer>
  )
}

const Option = (props) => {
  const theme = useTheme()
  return (
    <components.Option {...props}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <Typography sx={{ fontSize: '12px', color: props.isSelected ? theme.palette.grey[800] : theme.palette.grey[500], fontWeight: 500 }}>{props.data.label}</Typography>
        <Typography sx={{ fontSize: '11px', color: props.isSelected ? theme.palette.primary.main : theme.palette.grey[500], fontWeight: 500 }}>{props.data.count}</Typography>
      </Box>
    </components.Option>
  )
}

const OfferSelect: React.FC<{
  onChange: (selectedOffers: string[]) => void
  values: string[]
  offers: Offer[]
}> = ({ onChange, values = [], offers }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  
  const choices: Choice[] = offers.map((offer, index) => ({
    label: offer.name,
    value: offer.name,
    count: offer.count,
    id: index,
  }))

  return (
    <Select
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      width={'100%'}
      height="32px"
      options={choices}
      mainField="label"
      placeholder={t('stats.filters.offers')}
      onChange={(selectedValues) => {
        const selectedOffers = selectedValues.map(({ value }) => value)
        onChange(selectedOffers)
      }}
      components={{
        Option,
        Control: ({ children, ...props }) => {
          const { menuIsOpen, onMenuClose, onMenuOpen } = props.selectProps
          return (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <components.Control {...props}>
              <ClickAwayListener
                mouseEvent='onMouseDown'
                onClickAway={() => {
                  onMenuClose()
                }}
              >
                <Box
                  onClick={() => {
                    if (menuIsOpen) {
                      onMenuClose()
                    } else {
                      onMenuOpen()
                    }
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexGrow: 1,
                    gap: 7,
                    cursor: 'pointer'
                  }}
                >
                  {children}
                </Box>
              </ClickAwayListener>
            </components.Control>
          )
        },
        MenuList: ({ children, ...props }) => {
          return (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            <components.MenuList {...props}>
              {children}
              <Box
                ml={1}
                mt={1}
                color={'grey.600'}
                fontSize={12}
                fontWeight={500}
                onClick={() => props.setValue([])}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.grey[800],
                  },
                }}
              >
                {t('actions.deselect_all')}
              </Box>
            </components.MenuList>
          )
        },
        DropdownIndicator,
        ValueContainer,
        Placeholder: (props) => (
          <Box>
            <components.Placeholder {...props} />
          </Box>
        ),
        IndicatorSeparator: null,
        ClearIndicator: null,
      }}
      value={choices.filter(choice => values.includes(choice.value))}
    />
  )
}

export default OfferSelect
